import * as THREE from 'three';
import { LightManager } from '../rendering/lighting/LightManager';
import { Terrain } from './terrain/Terrain';
import { Config } from '../core/Config';
import { ObjectManager } from '../objects/ObjectManager';
import { ObjectFactory } from '../objects/ObjectFactory';
import { Camera } from '../rendering/Camera';
import { LODSystem } from '../optimization/LODSystem';
import { CullingManager } from '../optimization/CullingManager';
import { DynamicObject } from '../objects/dynamic/DynamicObject';
import { SpatialManager } from '../optimization/SpatialManager';
import { GameState } from '../game/GameState';
import { ProjectileSystem } from '../projectiles/ProjectileSystem';

export class Scene {
  private scene: THREE.Scene;
  private lightManager: LightManager;
  private terrain: Terrain;
  private objectManager: ObjectManager;
  private objectFactory: ObjectFactory;
  private camera: Camera;
  private lodSystem: LODSystem;
  private cullingManager: CullingManager | undefined;
  private dynamicObjects: DynamicObject[] = [];
  private spatialManager: SpatialManager | undefined;
  private projectileSystem: ProjectileSystem | undefined;
  
  constructor(camera: Camera) {
    // Store the camera reference
    this.camera = camera;
    
    // Create the Three.js scene
    this.scene = new THREE.Scene();
    
    // Add fog for distance fading
    this.scene.fog = new THREE.FogExp2(Config.CLEAR_COLOR, 0.01);
    
    // Initialize lighting
    this.lightManager = new LightManager(this.scene);
    
    // Initialize terrain
    this.terrain = new Terrain(this.scene);
    
    // Initialize object systems
    this.objectManager = new ObjectManager(this.scene);
    
    // Get GameState instance
    const gameState = GameState.getInstance();
    
    // Initialize ObjectFactory with GameState
    this.objectFactory = new ObjectFactory(this.objectManager, this.scene, gameState);
    
    // Initialize the LOD system
    this.lodSystem = LODSystem.getInstance(camera);
    
    // Initialize the projectile system
    this.projectileSystem = new ProjectileSystem(this.scene, camera.getThreeCamera(), this.objectManager);
    
    // Configure LOD system
    if (Config.LOD_UPDATE_FREQUENCY) {
      this.lodSystem.setUpdateFrequency(Config.LOD_UPDATE_FREQUENCY);
    }
    
    // Add axes helper for debugging
    if (Config.SHOW_AXES_HELPER) {
      const axesHelper = new THREE.AxesHelper(Config.AXES_HELPER_SIZE);
      this.scene.add(axesHelper);
    }
  
    // Create all game objects
    this.createGameObjects();
    
    console.log('Scene initialized');
  }
  
  // Initialize the culling manager when renderer is available
  public initializeCullingManager(renderer: THREE.WebGLRenderer): void {
    // Create culling manager using the getInstance pattern
    this.cullingManager = CullingManager.getInstance(
      this.camera, 
      this.scene, 
      renderer, 
      this.objectManager
    );
    
    console.log('Culling manager initialized');
    
    // Get the spatial manager from the object manager
    this.spatialManager = this.objectManager.getSpatialManager();
    
    // Connect frustum culling to spatial manager for priority adjustments
    if (this.spatialManager && this.cullingManager) {
      const frustumCulling = this.cullingManager.getFrustumCullingSystem();
      if (frustumCulling) {
        this.spatialManager.setFrustumCulling(frustumCulling);
        console.log('Connected frustum culling to spatial manager for dynamic priority adjustments');
      }
    }
  }
  
  /**
   * Create all game objects through the unified ObjectFactory
   */
  private createGameObjects(): void {
    console.log('Creating game objects...');
    
    // Use the factory to create all objects
    const objects = this.objectFactory.createAllObjects();
    
    console.log(`Created ${objects.length} total objects`);
  }
  
  /**
   * Reset bread loaf and toasters
   */
  public resetToasters(): void {
    this.objectFactory.resetToasters();
  }
  
  public update(deltaTime: number): void {
    // Update all systems
    
    // Update the object manager (which will update dynamic objects and their positions)
    this.objectManager.update(deltaTime);
    
    // Update spatial management
    if (this.spatialManager) {
      this.spatialManager.update(deltaTime);
    }
    
    // Update projectile system
    if (this.projectileSystem) {
      this.projectileSystem.update(deltaTime);
    }
    
    // Update LOD system - takes no parameters
    this.lodSystem.update();
    
    // Update culling
    if (this.cullingManager) {
      this.cullingManager.update();
    }
    
    // Update terrain
    this.terrain.update(deltaTime);
    
    // Update lighting
    this.lightManager.update(deltaTime);
    
    // DEBUG: Log memory stats every second
    this.updateDebugCounter = (this.updateDebugCounter || 0) + deltaTime;
    if (this.updateDebugCounter >= 1.0) {
      this.updateDebugCounter = 0;
      this.logMemoryStats();
    }
  }
  
  /**
   * Log memory statistics to help debug leaks
   */
  private updateDebugCounter: number = 0;
  private logMemoryStats(): void {
    if (!this.spatialManager) return;
    
    const stats = this.spatialManager.getStats();
    console.log(`🔍 MEMORY STATS: Dynamic Objects: ${stats.dynamicCount}, Dirty: ${stats.dirtyCount}, Quadtree Objects: ${stats.objects}, Nodes: ${stats.nodes}`);
    
    // Check for objects outside world bounds
    let outsideBounds = 0;
    const worldSize = Config.TERRAIN_SIZE;
    const halfSize = worldSize / 2;
    
    this.dynamicObjects.forEach(obj => {
      const pos = obj.getPosition();
      if (Math.abs(pos.x) > halfSize || Math.abs(pos.z) > halfSize) {
        outsideBounds++;
      }
    });
    
    if (outsideBounds > 0) {
      console.warn(`⚠️ Found ${outsideBounds} objects outside world bounds!`);
    }
  }
  
  public getThreeScene(): THREE.Scene {
    return this.scene;
  }
  
  public getTerrain(): Terrain {
    return this.terrain;
  }
  
  public getObjectManager(): ObjectManager {
    return this.objectManager;
  }
  
  public getObjectFactory(): ObjectFactory {
    return this.objectFactory;
  }
  
  public getCamera(): Camera {
    return this.camera;
  }
  
  public getLODSystem(): LODSystem {
    return this.lodSystem;
  }
  
  public performSpatialQuery(): void {
    try {
      const camera = this.camera.getThreeCamera();
      
      const center = new THREE.Vector3(camera.position.x, 0, camera.position.z);
      const radius = 10;
      
      console.log('Starting spatial query at position:', center, 'with radius:', radius);
      console.time('Spatial Query');
      const nearbyObjects = this.objectManager.getObjectsInRadius(center, radius);
      console.timeEnd('Spatial Query');
      
      console.log(`Found ${nearbyObjects.length} objects within ${radius} units of camera:`, nearbyObjects);
      
      if (nearbyObjects.length === 0) {
        console.log('No objects found. Try moving the camera closer to the cubes.');
        return;
      }
      
      const highlightMaterial = new THREE.MeshStandardMaterial({ 
        color: 0xffff00, 
        emissive: 0x555500 
      });
      
      const originalMaterials = new Map();
      
      nearbyObjects.forEach(obj => {
        const mesh = obj.getMesh();
        if (mesh) {
          originalMaterials.set(obj.getId(), mesh.material);
          mesh.material = highlightMaterial;
        }
      });
      
      setTimeout(() => {
        nearbyObjects.forEach(obj => {
          const mesh = obj.getMesh();
          if (mesh && originalMaterials.has(obj.getId())) {
            mesh.material = originalMaterials.get(obj.getId());
          }
        });
      }, 1000);
    } catch (error) {
      console.error('Error in spatial query:', error);
    }
  }
  
  /**
   * Get the culling manager
   */
  public getCullingManager(): CullingManager | undefined {
    return this.cullingManager;
  }
  
  /**
   * Get the projectile system
   */
  public getProjectileSystem(): ProjectileSystem | undefined {
    return this.projectileSystem;
  }
}
